// Library methods
import { useCallback } from "react";
import { DateTime } from "luxon";

// Constants
import { ExamCardSectionNameType } from "src/constants/enums";

// Types
import { ClinicPackageType } from "src/services/clinics/types";
import { AddExamSectionType, ExamType } from "src/services/exams/types";

// Hooks
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useAddExam } from "src/services/exams/hooks/useAddExam";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

export const useAddPresetExams = () => {
  const { clinicInfo } = useClinicContext();
  const { unlinkedDevices } = useDevicesContext();

  const { mutateAsync: linkDeviceMutate } = useLinkExamDevice(clinicInfo?.id);
  const { mutateAsync: addExamMutation } = useAddExam(clinicInfo?.id);

  const getFormattedExamSections = useCallback((presetExam: ExamType) => {
    let examSections: {
      [key in ExamCardSectionNameType]?: Array<AddExamSectionType>;
    } = {};

    const sectionNames = Object.keys(presetExam).filter((key) =>
      key.includes("Sections")
    ) as Array<ExamCardSectionNameType>;

    sectionNames.forEach((sectionName) => {
      examSections[sectionName] = [...(presetExam?.[sectionName] ?? [])];
    });

    return examSections;
  }, []);

  const addPresetExam = useCallback(
    async ({
      patientId,
      presetExam,
    }: {
      patientId: string;
      presetExam: ClinicPackageType;
    }) => {
      const examContent = JSON.parse(presetExam?.content);
      const examSections = getFormattedExamSections(examContent);

      const today =
        DateTime.now()
          .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
          .startOf("day")
          .toUTC()
          .toISO() ?? "";

      const examData = {
        scheduledDate: today,
        ...examSections,
      };

      const newExam = await addExamMutation({
        patientId,
        examData,
      });

      // if there is only 1 device available, auto-link the exam to it
      if (newExam && unlinkedDevices?.length === 1) {
        await linkDeviceMutate({
          exam: newExam,
          deviceId: unlinkedDevices?.[0]?.id,
        });
      }
    },
    [
      getFormattedExamSections,
      linkDeviceMutate,
      addExamMutation,
      unlinkedDevices,
      clinicInfo?.timezone,
    ]
  );

  return { addPresetExam };
};
